import * as React from "react";
import { useState } from "react";
import { PanelLeft } from "lucide-react";

import { cn } from "@/lib/utils";
import type { GuideId } from "@/guide/guideIds";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export enum ItemType {
  item = "item",
  heading = "heading",
}

/** Eintrag im aufklappbaren Untermenü eines Bereichs (z. B. ein Reiter des Bereichs) — nur Text, ohne Symbol. */
export interface SideBarSubItem {
  key: string;
  text: String;
  /** Guide id (portal guide, `src/guide/guideIds.ts`) — becomes `data-guide-id` on the row. */
  guideId?: GuideId;
  /** Ob dieser Unterpunkt gerade geöffnet ist — entscheidet der Aufrufer, die Sidebar kennt die Adresse nicht. */
  active?: boolean;
  action: () => void;
}

export interface SideBarItem {
  type: ItemType;
  key: string;
  text: String;
  /** Optional icon — typically a lucide-react component, e.g. <Settings />. */
  icon?: React.ReactNode;
  action?: () => void;
  /**
   * Untermenü: Der Eintrag klappt dann auf, statt nur zu wählen. Nur die `AppSidebar` zeigt es;
   * die ältere `SideBar` ignoriert es.
   */
  children?: SideBarSubItem[];
}

interface Properties {
  items: SideBarItem[];
  setItem?: (item: string) => void;
  shown: boolean;
  shownChanged?: (shown: boolean) => void;
  /**
   * Aktiver Eintrag, von außen kontrolliert (z. B. aus dem ?panel=-Parameter).
   * Ohne diese Prop verhält sich die Sidebar wie bisher und führt die Auswahl
   * intern (erster Eintrag aktiv).
   */
  selectedKey?: string;
  /** Screenreader-Titel des mobilen Sheets, z. B. der Seitenname. */
  sheetTitle?: string;
}

/**
 * Navigations-Sidebar der Settings-/Admin-Seiten, mit shadcn-Sidebar-Tokens.
 * Desktop: In-flow-Spalte neben ContentPanel, Collapse über animierte Breite —
 * die Aufrufseite behält die Kontrolle über `shown`. Mobil (<768px): dieselben
 * Einträge in einem `Sheet` von links; ein Tap auf einen Eintrag schließt es.
 */
export default function SideBar(properties: Properties) {
  const isMobile = useIsMobile();
  const [internalSelectedKey, setInternalSelectedKey] = useState<string | undefined>(
    () => {
      const firstItem = properties.items.find(
        (item) => item.type === ItemType.item,
      );
      return (firstItem ?? properties.items[0])?.key;
    },
  );
  const selectedKey = properties.selectedKey ?? internalSelectedKey;

  // Enable the width transition only after the first paint. Without this gate,
  // any layout reflow during mount (CSS / fonts / hot reload settling) is
  // animated by the browser, producing a visible left-ward slide of the items.
  const [transitionsEnabled, setTransitionsEnabled] = React.useState(false);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setTransitionsEnabled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const selectItem = (item: SideBarItem) => {
    setInternalSelectedKey(item.key);
    properties.setItem?.(item.key);
    item.action?.();
    if (isMobile) {
      properties.shownChanged?.(false);
    }
  };

  const navigation = (
    <nav className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
      {properties.items.map((item, index) =>
        item.type === ItemType.heading ? (
          <div
            key={item.key}
            className={cn(
              "px-2 text-xs font-medium uppercase tracking-wide text-sidebar-foreground/60",
              index > 0 && "mt-3",
            )}
          >
            {item.text}
          </div>
        ) : (
          <button
            key={item.key}
            type="button"
            data-active={selectedKey === item.key || undefined}
            onClick={() => selectItem(item)}
            className={cn(
              "flex h-9 w-full min-w-0 cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent px-3 text-left text-sm max-sm:h-11",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
              "data-[active]:bg-sidebar-accent data-[active]:font-medium data-[active]:text-sidebar-accent-foreground",
            )}
          >
            {/* lucide-react renders <svg width="24" height="24"> by default
                — force size via prop so it's correct on first paint, no
                CSS override needed. */}
            {React.isValidElement(item.icon)
              ? React.cloneElement(item.icon as React.ReactElement<any>, {
                  size: 16,
                  className: cn(
                    "shrink-0",
                    (item.icon as React.ReactElement<any>).props?.className,
                  ),
                })
              : item.icon}
            <span className="truncate">{item.text}</span>
          </button>
        ),
      )}
    </nav>
  );

  if (isMobile) {
    return (
      <Sheet open={properties.shown} onOpenChange={(open) => properties.shownChanged?.(open)}>
        <SheetContent
          side="left"
          className="flex w-72 max-w-[85vw] flex-col gap-0 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{properties.sheetTitle || "Navigation"}</SheetTitle>
          </SheetHeader>
          <div className="pt-10" />
          {navigation}
        </SheetContent>
      </Sheet>
    );
  }

  // Inline width keeps the collapse animation identical to the previous
  // behavior (the call site animates between 16rem and 0).
  const asideStyle: React.CSSProperties = {
    width: properties.shown ? "16rem" : "0",
    transition: transitionsEnabled ? "width 200ms linear" : "none",
  };

  return (
    <aside
      style={asideStyle}
      className="shrink-0 self-stretch overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
    >
      <div className="flex h-full w-64 flex-col">
        <div className="flex justify-end p-1">
          <button
            type="button"
            aria-label="Toggle sidebar"
            onClick={() => properties.shownChanged?.(!properties.shown)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            <PanelLeft size={16} />
          </button>
        </div>
        {navigation}
      </div>
    </aside>
  );
}
